import { Component, ChangeDetectionStrategy, input, model, signal } from '@angular/core';

/**
 * Zone de dépôt de fichier (glisser-déposer ou clic pour parcourir),
 * mono-fichier. Le fichier sélectionné est exposé en two-way binding.
 *
 * Usage : <idra-dropzone accept=".pdf,.txt" [(file)]="selectedFile" />
 */
@Component({
  selector: 'idra-dropzone',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: block;
    }

    .dropzone {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      min-height: 180px;
      padding: 24px;
      border: 2px dashed var(--idra-border-strong);
      border-radius: var(--idra-radius-xl);
      background: var(--idra-surface-50);
      color: var(--idra-text-secondary);
      cursor: pointer;
      text-align: center;
      transition:
        border-color 0.15s ease,
        background 0.15s ease;

      &:hover,
      &:focus-visible {
        border-color: var(--idra-primary);
        background: var(--idra-surface-hover);
        outline: none;
      }

      &.dragover {
        border-color: var(--idra-primary);
        background: var(--idra-primary-soft);
      }

      &.disabled {
        opacity: 0.6;
        cursor: not-allowed;
        pointer-events: none;
      }

      .icon {
        font-size: 28px;
        color: var(--idra-primary);
      }

      .hint {
        margin: 0;
        font-size: 13px;
        color: var(--idra-text-muted);
      }
    }

    .selected {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-weight: 700;
      color: var(--idra-text);

      button {
        display: inline-flex;
        align-items: center;
        border: none;
        background: none;
        padding: 4px;
        cursor: pointer;
        color: var(--idra-text-muted);
        border-radius: var(--idra-radius-full);

        &:hover {
          color: var(--idra-danger);
          background: var(--idra-danger-bg);
        }
      }
    }
  `,
  template: `
    <input
      #input
      type="file"
      hidden
      [accept]="accept()"
      [disabled]="disabled()"
      (change)="onFileSelected($event)"
    />
    <div
      class="dropzone"
      role="button"
      tabindex="0"
      [class.dragover]="dragover()"
      [class.disabled]="disabled()"
      [attr.aria-label]="label()"
      (click)="input.click()"
      (keydown.enter)="input.click()"
      (keydown.space)="$event.preventDefault(); input.click()"
      (dragover)="onDragOver($event)"
      (dragleave)="dragover.set(false)"
      (drop)="onDrop($event)"
    >
      @if (file(); as selected) {
        <i class="icon pi pi-file" aria-hidden="true"></i>
        <span class="selected">
          {{ selected.name }}
          <button
            type="button"
            aria-label="Retirer le fichier"
            (click)="$event.stopPropagation(); clear()"
          >
            <i class="pi pi-times" aria-hidden="true"></i>
          </button>
        </span>
        <p class="hint">Déposez ou choisissez un autre fichier pour le remplacer.</p>
      } @else {
        <i class="icon pi pi-cloud-upload" aria-hidden="true"></i>
        <span>{{ label() }}</span>
        @if (hint(); as text) {
          <p class="hint">{{ text }}</p>
        }
      }
    </div>
  `,
})
export class IdraDropzone {
  /** Extensions/types acceptés, au format de l'attribut `accept` natif. */
  readonly accept = input<string>('');
  readonly disabled = input<boolean>(false);
  readonly label = input<string>('Glissez-déposez un fichier ici, ou cliquez pour parcourir');
  /** Texte secondaire (formats acceptés, taille maximale…). */
  readonly hint = input<string | null>(null);
  /** Fichier sélectionné (two-way binding). */
  readonly file = model<File | null>(null);

  protected dragover = signal(false);

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.file.set(input.files?.[0] ?? null);
    // Réinitialise l'input natif : resélectionner le même fichier redéclenche `change`.
    input.value = '';
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (!this.disabled()) {
      this.dragover.set(true);
    }
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragover.set(false);
    if (this.disabled()) {
      return;
    }
    const dropped = event.dataTransfer?.files?.[0] ?? null;
    if (dropped) {
      this.file.set(dropped);
    }
  }

  protected clear(): void {
    this.file.set(null);
  }
}
